import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { WaveformChart, type WaveformData } from "@/components/WaveformChart";
import { Button } from "@/components/ui/button";
import { HarmonicSpectrumChart } from "@/components/HarmonicSpectrumChart";
import { InterharmonicChart } from "@/components/InterharmonicChart";
import { SystemSummaryPanel } from "@/components/SystemSummaryPanel";
import type { HarmonicAnalysis } from "@/lib/types";

// Raw row from GET /api/pq/events/{id} — includes pe.* + pd.* + m.* fields.
// Kept loose; route is admin-facing in Ship 2A (clients still 404 on drops).
type EventDetailRow = Record<string, any>;

const DECISION_STYLES: Record<string, string> = {
  keep: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  promoted: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  drop: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  discard_non_swell: "bg-muted text-muted-foreground border-border",
};

function Badge({ decision }: { decision: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
        DECISION_STYLES[decision] ?? "bg-muted text-muted-foreground border-border"
      )}
    >
      {decision}
    </span>
  );
}

function KV({ k, v, mono = false }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 text-sm border-b last:border-0">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className={cn("text-right", mono && "font-mono text-xs")}>{v ?? "—"}</dd>
    </div>
  );
}

function fmt(v: any, digits = 2, unit = ""): string {
  if (v == null) return "—";
  if (typeof v !== "number") return String(v);
  return `${v.toFixed(digits)}${unit}`;
}

// Phase l1/l2/l3 → fastlog current-channel key (see fastlog_parser.extract_series).
const I_CHANNEL: Record<string, string> = { l1: "Ia", l2: "Ib", l3: "Ic" };

// Per-phase current max + mean from the fastlog series. The fastlog is
// ½-cycle RMS — so its max is the real "how high did RMS current get"
// figure, unlike the COMTRADE waveform's instantaneous sample peak. Returns
// nulls when there's no fastlog series (caller falls back to the COMTRADE
// max_current_* column).
function phaseCurrentStats(
  fastlog: WaveformData | null,
  phase: string,
): { max: number | null; avg: number | null } {
  const vals = fastlog?.channels?.[I_CHANNEL[phase]]?.values?.filter(
    (v): v is number => v != null
  );
  if (!vals || vals.length === 0) return { max: null, avg: null };
  return {
    // Reduce, never `Math.max(...vals)` — a long fastlog series spread as
    // function arguments overflows the call stack (RangeError).
    max: vals.reduce((m, v) => Math.max(m, v), -Infinity),
    avg: vals.reduce((a, b) => a + b, 0) / vals.length,
  };
}

export function EventDetail() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Back returns to wherever the operator came from — the PQ-events section of
  // a system's detail page, or the event feed — not always the feed root.
  // navigate(-1) pops history (so ServiceDetail restores its scroll); a direct
  // route is the fallback when this view was opened cold (deep link/refresh).
  const goBack = () => {
    if (location.key !== "default") navigate(-1);
    else navigate("/feed");
  };

  // Open at the top — an SPA route change keeps the window scroll, so a PQ
  // event tapped far down a detail page would otherwise open mid-view.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [eventId]);

  const [row, setRow] = useState<EventDetailRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<number | null>(null);
  const [waveform, setWaveform] = useState<WaveformData | null>(null);
  const [waveformErr, setWaveformErr] = useState<string | null>(null);
  const [waveformLoading, setWaveformLoading] = useState(false);
  // Shared zoom state for fastlog ↔ waveform charts. Both share the same
  // time origin (event capture window), so a single x-range applies to both.
  const [syncRange, setSyncRange] = useState<[number, number] | null>(null);
  // IEC 61000-4-7 harmonic analysis (lazy — fetched after the event loads).
  const [analysis, setAnalysis] = useState<HarmonicAnalysis | null>(null);
  const [analysisErr, setAnalysisErr] = useState<string | null>(null);
  const [selectedChannel, setSelectedChannel] = useState(0);

  // Memoize the derived fastlog series. Without this it'd be a fresh
  // reference every render, and WaveformChart's useEffect([data]) would
  // tear down + rebuild uPlot on every state change — including the
  // setSyncRange that fires on wheel zoom, which made the chart visibly
  // snap back to full extent before the syncRange effect re-applied.
  const fastlog = useMemo<WaveformData | null>(
    () => (row?.metadata?.fastlog_series as WaveformData | undefined) ?? null,
    [row]
  );

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    api
      .get<EventDetailRow>(`/api/pq/events/${encodeURIComponent(eventId)}`)
      .then((r) => !cancelled && setRow(r))
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError) {
          setStatus(e.status);
          setError(e.status === 404 ? "Event not found" : String(e.body));
        } else {
          setError(String(e));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  // Auto-fetch waveform once the event loads successfully.
  useEffect(() => {
    if (!eventId || !row) return;
    if (row.parse_status !== "ok") return;
    let cancelled = false;
    setWaveformLoading(true);
    setWaveformErr(null);
    api
      .get<WaveformData & { pending_forward?: boolean; detail?: string }>(
        `/api/pq/events/${encodeURIComponent(eventId)}/waveform`
      )
      .then((w) => {
        if (cancelled) return;
        // 200 with pending_forward=true → edge captured but cloud hasn't
        // received the full COMTRADE pair yet (MQTT placeholder).
        if (w.pending_forward) {
          setWaveformErr(
            w.detail ??
              "Waveform pending forward — edge captured the event; cloud is waiting for the outbox forwarder (~120s cycle)."
          );
          return;
        }
        setWaveform(w);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError) {
          if (e.status === 404) setWaveformErr("Waveform file missing");
          else if (e.status === 422) setWaveformErr("COMTRADE parse failed");
          else setWaveformErr(String(e.body));
        } else {
          setWaveformErr(String(e));
        }
      })
      .finally(() => !cancelled && setWaveformLoading(false));
    return () => {
      cancelled = true;
    };
  }, [eventId, row]);

  // Fetch the harmonic analysis once the event has parsed. The endpoint
  // analyses the .cvf on first request, so this is a one-off per event.
  useEffect(() => {
    if (!eventId || !row || row.parse_status !== "ok") return;
    let cancelled = false;
    setAnalysis(null);
    setAnalysisErr(null);
    setSelectedChannel(0);
    api
      .get<HarmonicAnalysis>(
        `/api/pq/events/${encodeURIComponent(eventId)}/analysis`
      )
      .then((a) => {
        if (cancelled) return;
        if (a.pending_forward) {
          setAnalysisErr(a.detail ?? "Analysis pending — waveform not yet on cloud.");
          return;
        }
        setAnalysis(a);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError) {
          setAnalysisErr(
            e.status === 422 ? "Harmonic analysis failed" : String(e.body)
          );
        } else {
          setAnalysisErr(String(e));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, row]);

  const retryWaveform = () => {
    setWaveform(null);
    setWaveformErr(null);
    setRow((r) => (r ? { ...r } : r)); // re-trigger effect
  };

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          {status === 404 ? "Event not found" : "Error"}
        </h1>
        <p className="text-sm text-muted-foreground">{error}</p>
        <button
          type="button"
          onClick={goBack}
          className="text-sm underline text-primary"
        >
          ← Back
        </button>
      </div>
    );
  }
  if (!row) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const decision = row.decision ?? "keep";
  const voltages = ["l1", "l2", "l3"];
  // COMTRADE files exist whenever the ingest parse succeeded — all event types,
  // not just swell. parse_status tracks the original ingest-time parse.
  const hasComtrade = row.parse_status === "ok";

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">
            {row.service_name ?? row.system_id}
          </h1>
          <Badge decision={decision} />
        </div>
        <p className="text-sm text-muted-foreground font-mono">{row.event_id}</p>
        <button
          type="button"
          onClick={goBack}
          className="text-sm underline text-primary"
        >
          ← Back
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Event</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <dl>
              <KV k="Trigger" v={row.trigger_ts} mono />
              <KV k="Type" v={row.event_type} />
              <KV k="Severity" v={row.severity} />
              <KV k="Duration" v={fmt(row.duration_ms, 0, " ms")} />
              <KV k="Depth %" v={fmt(row.depth_pct, 1, "%")} />
              <KV k="Magnitude %" v={fmt(row.magnitude_pct, 1, "%")} />
              <KV k="Frequency" v={fmt(row.frequency_hz, 3, " Hz")} />
              <KV k="Parse status" v={row.parse_status} />
              <KV
                k="Phases affected"
                v={
                  row.phase_affected?.length
                    ? row.phase_affected.join(", ")
                    : "—"
                }
              />
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Disposition</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <dl>
              <KV k="Decision" v={<Badge decision={decision} />} />
              <KV k="Keep reason" v={row.keep_reason} />
              <KV k="Multiplier" v={fmt(row.multiplier, 2, "×")} />
              <KV k="Rule version" v={row.rule_version} />
              <KV k="Rule hash" v={row.rule_hash} mono />
              <KV k="Baseline source" v={row.baseline_source} />
              <KV
                k="Baseline age"
                v={fmt(row.baseline_sample_age_seconds, 1, " s")}
              />
              <KV k="Classification" v={row.classification_status} />
              <KV
                k="Window / Service"
                v={
                  row.window_id ? (
                    <span>
                      {row.service_name ?? "—"}{" "}
                      <span className="text-muted-foreground text-xs">
                        · {row.window_id}
                      </span>
                    </span>
                  ) : (
                    "—"
                  )
                }
              />
            </dl>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Phase measurements</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Phase</TableHead>
                <TableHead className="text-right">Pre V</TableHead>
                <TableHead className="text-right">Min V</TableHead>
                <TableHead className="text-right">Pre I</TableHead>
                <TableHead className="text-right">Avg I</TableHead>
                <TableHead className="text-right">Max I</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {voltages.map((p) => {
                const iStats = phaseCurrentStats(fastlog, p);
                return (
                  <TableRow key={p}>
                    <TableCell className="font-medium uppercase">{p}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmt(row[`pre_voltage_${p}`], 1, " V")}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmt(row[`min_voltage_${p}`], 1, " V")}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmt(row[`pre_current_${p}`], 1, " A")}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmt(iStats.avg, 1, " A")}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {/* ½-cycle RMS max from fastlog; COMTRADE waveform-peak
                          fallback only when there's no fastlog series. */}
                      {fmt(iStats.max ?? row[`max_current_${p}`], 1, " A")}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <p className="mt-2 text-[11px] text-muted-foreground">
            {fastlog
              ? "Avg / Max I are ½-cycle RMS over the event window (fastlog)."
              : "Max I is the COMTRADE waveform sample peak — no fastlog ½-cycle data for this event."}
          </p>
        </CardContent>
      </Card>

      {(() => {
        return (
          <>
            {fastlog && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Fastlog (½-cycle RMS)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <WaveformChart
                    data={fastlog}
                    height={260}
                    syncRange={syncRange}
                    onSyncRangeChange={setSyncRange}
                    /* The trim recommendation is COMTRADE-derived; the fastlog
                       chart shares the ms x-axis so it shows the same band. */
                    recommendedTrim={waveform?.recommended_trim ?? null}
                    serviceCapacityAmps={
                      typeof row.service_capacity_amps === "number"
                        ? row.service_capacity_amps
                        : null
                    }
                  />
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Waveform (point-on-wave)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {!hasComtrade ? (
                  <div className="text-sm text-muted-foreground py-4">
                    No waveform available for this event (parse_status=
                    {row.parse_status ?? "—"}).
                  </div>
                ) : waveformLoading ? (
                  <div className="text-sm text-muted-foreground py-4">
                    Parsing COMTRADE file…
                  </div>
                ) : waveformErr ? (
                  <div className="flex items-center justify-between py-4">
                    <div className="text-sm text-destructive">{waveformErr}</div>
                    <Button size="sm" variant="outline" onClick={retryWaveform}>
                      Retry
                    </Button>
                  </div>
                ) : waveform ? (
                  <WaveformChart
                    data={waveform}
                    syncRange={syncRange}
                    onSyncRangeChange={setSyncRange}
                    showHint={!fastlog /* fastlog above already shows it */}
                    recommendedTrim={waveform.recommended_trim ?? null}
                    serviceCapacityAmps={
                      typeof row.service_capacity_amps === "number"
                        ? row.service_capacity_amps
                        : null
                    }
                  />
                ) : null}
              </CardContent>
            </Card>
          </>
        );
      })()}

      {hasComtrade && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Harmonics &amp; Power Quality{" "}
              <span className="text-muted-foreground font-normal text-sm">
                · IEC 61000-4-7
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-6">
            {analysisErr ? (
              <div className="text-sm text-muted-foreground py-2">
                {analysisErr}
              </div>
            ) : !analysis ? (
              <div className="text-sm text-muted-foreground py-2">
                Analysing harmonics…
              </div>
            ) : (
              <>
                <SystemSummaryPanel analysis={analysis} />
                {analysis.channels.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-muted-foreground">
                        Channel:
                      </span>
                      {analysis.channels.map((c, i) => (
                        <button
                          key={c.channel_name}
                          onClick={() => setSelectedChannel(i)}
                          className={cn(
                            "rounded-md border px-2 py-0.5 text-xs transition-colors",
                            i === selectedChannel
                              ? "bg-secondary text-secondary-foreground border-border"
                              : "text-muted-foreground hover:text-foreground border-border"
                          )}
                        >
                          {c.channel_name}
                        </button>
                      ))}
                    </div>
                    {analysis.channels[selectedChannel] && (
                      <>
                        <div>
                          <h3 className="text-sm font-medium mb-1">
                            Harmonic spectrum
                          </h3>
                          <HarmonicSpectrumChart
                            channel={analysis.channels[selectedChannel]}
                          />
                        </div>
                        <div>
                          <h3 className="text-sm font-medium mb-1">
                            Interharmonics
                          </h3>
                          <InterharmonicChart
                            channel={analysis.channels[selectedChannel]}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
